import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { logger } from '@zep/core-logger';
import { zusToast } from '@zep/toast';
import { Language } from '@zep/types';
import { Button, Textarea } from '@zep/ui';
import { StoreCategoryName, StoreQueryKeys } from '../../../../consts';
import useInput from '../../../../hooks/useInput';
import { usePostStoreItemReview, useStoreItemDetail } from '../../../../services';
import { AssetCommonHashIdProps } from '../../../../types/StoreTypes';
import StarRating from '../../common/StarRating';
import S from './StoreReviewWriter.module.scss';
const StoreReviewWriter = ({
  assetHashId,
  reviewTotalPage
}: StoreReviewWriterProps) => {
  const {
    user
  } = useAuth();
  const router = useRouter();
  const {
    t
  } = useTranslation(['store']);
  const queryClient = useQueryClient();
  const [rate, setRate] = useState(5);
  const [reviewText, handleTextOnChange, setReviewText] = useInput('');
  const [sendAssetData, setSendAssetData] = useState({
    assetHashId,
    text: reviewText,
    rating: rate
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isMyFirstReview, setIsMyFirstReview] = useState(false);
  const assetDetails = useStoreItemDetail(assetHashId, router.locale as Language, !!user?.isEdu);

  /**
   * 작성한 리뷰 POST 핸들함수
   */
  const createReviewMutation = usePostStoreItemReview(assetHashId, sendAssetData.text, sendAssetData.rating, !!user?.isEdu, {
    onSuccess: result => {
      if (result) {
        invalidateQueries();
        logger.track('store_detail_review_complete', {
          type: StoreCategoryName[assetDetails.data?.item.category ?? 0]
        });
        setIsDirty(false);

        // 댓글 작성 및 수정 완료 시 작성완료 토스트 띄워주기
        zusToast.success(isMyFirstReview ? t('store:store_toast_review_write_success') : t('store:store_toast_review_edit_success'));

        // 이제 작성했으니 첫번째 리뷰 아님
        setIsMyFirstReview(false);
      }
    },
    onError: (error: Error) => {
      // 댓글 작성 및 수정 완료 시 작성완료 토스트 띄워주기
      zusToast.error(error.message);
    }
  });
  const invalidateQueries = () => {
    for (let page = 1; page <= (reviewTotalPage ?? 0); page++) {
      void queryClient.invalidateQueries([StoreQueryKeys.ASSET_REVIEW_LIST_GET, assetHashId, page]);
    }

    // 리뷰 총점 계산을 다시 하기 위해 해당 쿼리키(StoreReviewLayout)를 무효화(invalidate) 시켜줌.
    void queryClient.invalidateQueries([StoreQueryKeys.ASSET_DETAIL_GET, assetHashId]);
  };

  /**
   * 상황에 따른 초기 별활성화 갯수 리턴
   */
  const checkFilledCount = useMemo(() => {
    // 이미 남긴 평점이 있다면 해당 평점이 default
    if (assetDetails.data?.myComment?.rating) {
      return assetDetails.data?.myComment.rating;
    }
    if (reviewText && assetDetails.data?.myComment?.text) {
      return 5;
    }
    if (!user || !assetDetails.data?.purchased) {
      return 0;
    }
    if (reviewText === '' && !assetDetails.data?.myComment?.text) {
      return 5;
    }
    return 5;
  }, [assetDetails.data?.myComment?.rating, assetDetails.data?.myComment?.text, assetDetails.data?.purchased, reviewText, user]);

  /**
   * 구매한 경우에 리뷰 작성할 수 있음
   * 그렇지 않다면 disabled
   */
  const checkOnChange = () => {
    if (assetDetails.data?.purchased) {
      return handleTextOnChange;
    }
    return;
  };

  /**
   * 별점이 눌려야하는 상황인지 체크(구매했는지 쳌)
   */
  const checkIsPurchased = !user || !assetDetails.data?.purchased;

  /**
   * 상황에 따른 placeholder
   */
  const placeholderKey = useMemo(() => {
    // 미로그인, 미구매 상태라면 disabled
    if (!user) return 'store_review_please_login';
    // 내가 올린 에셋인 경우
    if (user?.hashId === assetDetails.data?.item.player?.hashId) return 'store_review_if_mine_impossible';
    // 구매 안한 경우 - disabled
    if (!assetDetails.data?.purchased) {
      return 'store_review_description';
    } else {
      if (!assetDetails.data?.myComment?.text) {
        // 댓글 작성 처음인 경우 - 작성하기
        return 'store_review_please_leave';
      }
    }
  }, [assetDetails.data?.item.player?.hashId, assetDetails.data?.myComment?.text, assetDetails.data?.purchased, user]);
  useEffect(() => {
    if (assetDetails?.data) {
      setRate(assetDetails.data.myComment?.rating ?? 5);
      setReviewText(assetDetails.data.myComment?.text ?? '');

      // 구매하자마자 첫 작성째인지 판별
      if (assetDetails.data?.myComment === null || assetDetails.data?.myComment === undefined) {
        setIsMyFirstReview(true);
        setIsDirty(true);
      } else {
        // 첫 작성이 아닌경우만 isDirty 타이밍 이슈로 timeout으로 맨뒤로 보내기
        setTimeout(() => setIsDirty(false), 100);
      }
    }
  }, [assetDetails.data, setReviewText]);
  useEffect(() => {
    setSendAssetData(prev => ({
      ...prev,
      text: reviewText,
      rating: rate
    }));
    setIsDirty(true);
  }, [reviewText, rate]);
  function handleClickLoginButton() {
    void router.push('/accounts/signin');
  }
  return <>
      <label className={S.review_title}>{t('store:store_write_review')}</label>
      <div className={S.star_rating}>
        <b>{t('store:store_star_rating')}</b>
        <StarRating starMaxCount={5} isClickable={!checkIsPurchased} filledCount={checkFilledCount} onRateChange={assetDetails.data?.purchased ? (r: number) => setRate(r) : () => {}} data-sentry-element="StarRating" data-sentry-source-file="StoreReviewWriter.tsx" />
      </div>
      <Textarea className={S.review_textarea} placeholder={
    // 이미 리뷰를 쓴 경우엔 키가 없어 placeholder 를 비운다.
    placeholderKey ? t(`store:${placeholderKey}`) : undefined} size={'sm'} isFullWidth={true} rows={3} onChange={checkOnChange()} value={reviewText} disabled={checkIsPurchased} maxLength={500} data-sentry-element="Textarea" data-sentry-source-file="StoreReviewWriter.tsx"></Textarea>
      {!(!user || !assetDetails.data?.purchased) && <Button className="w-[161px]" disabled={!isDirty}
    // eslint-disable-next-line zep/no-tanstack-mutate -- 성공·실패 처리는 뮤테이션 옵션에 있다. mutateAsync 전환은 별건.
    onClick={() => isDirty && createReviewMutation.mutate()}>
          {isMyFirstReview ? t('store:store_write_review') : t('store:store_edit')}
        </Button>}
      {!user && <Button size="xs" className={S.submit_button} onClick={handleClickLoginButton}>
          {t('store:store_review_login')}
        </Button>}
    </>;
};
export default StoreReviewWriter;
type StoreReviewWriterProps = AssetCommonHashIdProps & {
  reviewTotalPage?: number;
};